import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { Button } from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { cn } from '../../../lib/utils';
import type { SendIdentity } from './SendIdentitiesSection';

/** Matches backend EmailService.SENT_FOLDER_IDENTITY_DEFAULT */
const SENT_FOLDER_DEFAULT_ID = 0;

interface SentFolderIdentitiesSectionProps {
  accountId: number;
  accountEmail: string;
  sentFolderIdentityIds?: number[] | null;
  onMutate?: () => void;
}

function buildAllOptionIds(identities: SendIdentity[]): number[] {
  return [SENT_FOLDER_DEFAULT_ID, ...identities.map((i) => i.id)];
}

function setsEqual(a: Set<number>, b: number[]): boolean {
  if (a.size !== b.length) return false;
  return b.every((id) => a.has(id));
}

export const SentFolderIdentitiesSection: React.FC<SentFolderIdentitiesSectionProps> = ({
  accountId,
  accountEmail,
  sentFolderIdentityIds,
  onMutate,
}) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [identities, setIdentities] = useState<SendIdentity[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(() => new Set());

  const loadIdentities = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<SendIdentity[]>(`/emails/accounts/${accountId}/send-identities`);
      setIdentities(data);
    } catch {
      setIdentities([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void loadIdentities();
  }, [loadIdentities]);

  const allOptionIds = useMemo(() => buildAllOptionIds(identities), [identities]);

  useEffect(() => {
    const hasFilter = sentFolderIdentityIds != null && sentFolderIdentityIds.length > 0;
    if (hasFilter) {
      setSelected(new Set(sentFolderIdentityIds));
    } else {
      setSelected(new Set(allOptionIds));
    }
  }, [sentFolderIdentityIds, allOptionIds]);

  const options = useMemo(() => {
    const list: { id: number; label: string; address: string }[] = [
      {
        id: SENT_FOLDER_DEFAULT_ID,
        label: 'Default address',
        address: accountEmail,
      },
    ];
    for (const identity of identities) {
      list.push({
        id: identity.id,
        label: identity.name,
        address: identity.emailAddress?.trim() || accountEmail,
      });
    }
    return list;
  }, [accountEmail, identities]);

  const isNoFilter = selected.size === allOptionIds.length && allOptionIds.every((id) => selected.has(id));

  const toggleOption = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(allOptionIds));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = isNoFilter
        ? { sentFolderIdentityIds: [] }
        : { sentFolderIdentityIds: Array.from(selected) };
      await api.patch(`/email-accounts/${accountId}`, payload);
      showSuccess('Sent folder filter saved');
      onMutate?.();
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      showError(typeof msg === 'string' ? msg : 'Failed to save sent folder filter');
    } finally {
      setSaving(false);
    }
  };

  const savedIds = sentFolderIdentityIds ?? [];
  const hasSavedFilter = savedIds.length > 0;
  const dirty = hasSavedFilter
    ? !setsEqual(selected, savedIds)
    : !isNoFilter;

  if (loading) {
    return (
      <div className={cn('py-2 text-sm', colors.textMuted)} role="status">
        Loading send identities…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className={cn('text-sm', colors.textMuted)}>
        Επίλεξε ποιες διευθύνσεις αποστολής εμφανίζονται στα <strong>Sent</strong> και{' '}
        <strong>My sent</strong>. Όλες επιλεγμένες = χωρίς φίλτρο.
      </p>

      <div
        className={cn(
          'rounded-lg border p-3 space-y-2',
          colors.border,
          colors.surface,
        )}
        role="group"
        aria-label="Sent folder From addresses"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Show mail from
          </p>
          {!isNoFilter && (
            <button
              type="button"
              onClick={selectAll}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
            >
              Select all
            </button>
          )}
        </div>

        {options.map((opt) => (
          <div
            key={opt.id}
            className={cn(
              'flex items-center justify-between gap-3 rounded-md px-1 py-1.5',
              'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
            )}
          >
            <div className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className={cn('text-sm font-medium', colors.text)}>{opt.label}</span>
              <span
                className={cn(
                  'inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[11px] font-mono',
                  colors.border,
                  colors.textMuted,
                )}
              >
                {opt.address}
              </span>
            </div>
            <Switch
              checked={selected.has(opt.id)}
              onChange={() => toggleOption(opt.id)}
              size="sm"
              labelPosition="left"
              aria-label={`Show sent mail from ${opt.address}`}
            />
          </div>
        ))}

        {options.length === 1 && (
          <p className={cn('text-xs', colors.textMuted)}>
            Πρόσθεσε send identities παραπάνω για επιπλέον διευθύνσεις αποστολής.
          </p>
        )}

        {selected.size === 0 && (
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Επίλεξε τουλάχιστον μία διεύθυνση.
          </p>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={() => void handleSave()}
          disabled={saving || selected.size === 0 || !dirty}
        >
          {saving ? 'Saving…' : 'Save sent folder filter'}
        </Button>
      </div>
    </div>
  );
};
